import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Paper,
  Divider,
  TextField,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";
import WarningIcon from "@mui/icons-material/Warning";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { QRCodeSVG } from "qrcode.react";

const MfaSettings = () => {
  const [loading, setLoading] = useState(true);
  const [mfaStatus, setMfaStatus] = useState({ isEnabled: false, isConfigured: false });
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [mfaCode, setMfaCode] = useState("");

  useEffect(() => {
    fetchMfaStatus();
  }, []);

  const fetchMfaStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/mfa/status", {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch MFA status: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      setMfaStatus(data);
    } catch (err) {
      console.error("Error fetching MFA status:", err);
      setError("Failed to load MFA settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleMfa = async () => {
    setToggling(true);
    setError(null);
    try {
      if (!mfaStatus.isEnabled) {
        await fetchMfaSetup();
      } else {
        setToggling(true);
      }
    } catch (err) {
      setError(err.message || "Failed to update MFA settings. Please try again.");
      setToggling(false);
    }
  };

  const fetchMfaSetup = async () => {
    try {
      const response = await fetch("/api/mfa/setup", {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch MFA setup data: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      setSetupData(data);
    } catch (error) {
      console.error("Error fetching MFA setup data:", error);
      setError(error.message || "Failed to fetch MFA setup data");
    }
  };

  const handleSetupAuthenticator = () => {
    fetchMfaSetup();
  };

  const handleVerifyMfa = async () => {
    try {
      const response = await fetch("/api/mfa/verify", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: mfaCode }),
      });

      if (!response.ok) {
        throw new Error(`Failed to verify MFA code: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      console.log("Verify response:", data); // Debug log

      if (data.success) {
        if (mfaStatus.isEnabled) {
          // If disabling, proceed with disable
          await handleDisableMfa();
        } else {
          // If enabling, call the enable endpoint
          const enableResponse = await fetch("/api/mfa/enable", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ code: mfaCode }),
          });

          if (!enableResponse.ok) {
            throw new Error(`Failed to enable MFA: ${enableResponse.status}`);
          }

          const enableData = await enableResponse.json();
          console.log("Enable response:", enableData); // Debug log

          if (enableData.success) {
            // Fetch updated MFA status
            await fetchMfaStatus();
            setMfaCode("");
            setError("");
            setSetupData(null);
            setToggling(false);
          } else {
            setError(enableData.message || "Failed to enable MFA");
          }
        }
      } else {
        setError(data.message || "Invalid MFA code");
      }
    } catch (error) {
      console.error("Error in MFA verification/enable process:", error);
      setError(error.message || "Failed to complete MFA setup");
    }
  };

  const handleEnableMfa = async () => {
    try {
      const response = await fetch("/api/mfa/enable", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: mfaCode }),
      });

      if (!response.ok) {
        throw new Error(`Failed to enable MFA: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      if (data.success) {
        setMfaStatus((prev) => ({ ...prev, isEnabled: true }));
        setMfaCode("");
        setError("");
      } else {
        setError(data.message || "Failed to enable MFA");
      }
    } catch (error) {
      console.error("Error enabling MFA:", error);
      setError(error.message || "Failed to enable MFA");
    }
  };

  const handleDisableMfa = async () => {
    try {
      const response = await fetch("/api/mfa/disable", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ code: mfaCode }),
      });

      if (!response.ok) {
        throw new Error(`Failed to disable MFA: ${response.status}`);
      }

      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        throw new Error("Server returned non-JSON response");
      }

      const data = await response.json();
      if (data.success) {
        await fetchMfaStatus();
        setMfaCode("");
        setError("");
        setToggling(false);
      } else {
        setError(data.message || "Failed to disable MFA");
      }
    } catch (error) {
      console.error("Error disabling MFA:", error);
      setError(error.message || "Failed to disable MFA");
    }
  };

  // Add handleKeyDown function
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      handleVerifyMfa();
    }
  };

  const handleCancel = () => {
    setToggling(false);
    setMfaCode("");
    setError("");
    setSetupData(null);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: "bold" }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: "middle" }} />
        Multi-Factor Authentication
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* MFA Status Card */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" component="h2">
                  MFA Status
                </Typography>
                <Chip
                  icon={mfaStatus.isEnabled ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={mfaStatus.isEnabled ? "Enabled" : "Disabled"}
                  color={mfaStatus.isEnabled ? "success" : "error"}
                  variant="outlined"
                />
              </Box>
              <FormControlLabel
                control={<Switch checked={mfaStatus.isEnabled} onChange={handleToggleMfa} disabled={toggling} color="primary" />}
                label={
                  <Typography variant="body2" color="text.secondary">
                    {mfaStatus.isEnabled ? "Disable" : "Enable"} Multi-Factor Authentication
                  </Typography>
                }
              />
              {toggling && (
                <Box display="flex" alignItems="center" mt={1}>
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {mfaStatus.isEnabled ? "Disabling" : "Enabling"} MFA...
                  </Typography>
                </Box>
              )}
              {toggling && !mfaStatus.isEnabled && setupData && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Scan this QR code with your authenticator app:
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: "white", display: "inline-block", borderRadius: 1 }}>
                    <QRCodeSVG value={setupData.qrCodeUrl} size={200} level="H" includeMargin={true} />
                  </Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Or manually enter this key:
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {setupData.manualEntryKey}
                  </Typography>
                </Box>
              )}
              {toggling && (
                <Box mt={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    Enter the verification code from your authenticator app:
                  </Typography>
                  <TextField
                    type="text"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    margin="normal"
                    placeholder="Enter 6-digit code"
                    inputProps={{
                      maxLength: 6,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                    <Button variant="contained" onClick={handleVerifyMfa} fullWidth>
                      Verify
                    </Button>
                    <Button variant="outlined" onClick={handleCancel} fullWidth>
                      Cancel
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Setup Guide Card */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" component="h2" gutterBottom>
                Setup Guide
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  How to Enable MFA:
                </Typography>
                <Box component="ol" sx={{ pl: 2, mb: 2 }}>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Click the toggle switch above to enable MFA
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Install an authenticator app on your phone (Google Authenticator, Microsoft Authenticator, or Authy)
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Scan the QR code with your authenticator app
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Enter the 6-digit code from your authenticator app
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Click Verify to complete the setup
                  </Typography>
                </Box>

                <Typography variant="subtitle2" color="primary" gutterBottom>
                  How to Disable MFA:
                </Typography>
                <Box component="ol" sx={{ pl: 2 }}>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Click the toggle switch above to disable MFA
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Enter your current authenticator code
                  </Typography>
                  <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                    Click Verify to confirm
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Important:</strong> Keep your authenticator app secure. You'll need it to access your account.
                </Typography>
              </Alert>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Tips */}
        <Grid item xs={12}>
          <Paper elevation={1} sx={{ p: 2, bgcolor: "background.default" }}>
            <Typography variant="h6" gutterBottom>
              Security Tips
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Keep your authenticator app secure
                </Typography>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Use a reputable authenticator app
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Don't share your verification codes
                </Typography>
                <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                  Regularly review your security settings
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MfaSettings;
